import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { CheckCircle2, ExternalLink } from 'lucide-react';
import { Reveal } from '../Reveal';

import { useTypeformNavigation } from '../../hooks/useTypeformNavigation';
import { useConditionalLogic } from '../../hooks/useConditionalLogic';
import { useFormScoring } from '../../hooks/useFormScoring';
import { TypeformTransition } from './TypeformTransition';
import { ScoreResult } from './ScoreResult';
import { TypeformProgress } from './TypeformProgress';
import { TypeformNavigation } from './TypeformNavigation';
import {
  TextField,
  TextAreaField,
  SelectField,
  CheckboxField,
  RadioField,
  DateField,
  FileUploadField,
  OpinionScaleField,
  NPSField,
  RatingField,
  PictureChoiceField,
  YesNoField,
  StatementField,
  WelcomeScreenField,
  ThankYouScreenField,
  RedirectUrlField,
} from './fields';
import type { FormField, FieldType, WelcomeScreenConfig, RedirectUrlConfig, LogoPosition, FieldAlignment } from '../../types/forms';
import type { Form } from '../../types/forms';
import { DEFAULT_THEME_CONFIG, normalizeFieldOption } from '../../types/forms';
import { getButtonStyles } from '../../lib/themeUtils';
import { useThemeFont } from '../../hooks/useThemeFont';
import { resolvePages } from '../../lib/formPages';
import { MultiQuestionSlide } from './MultiQuestionSlide';
import { validateFieldValue } from '../../lib/formValidation';
import { SaveConfirmation } from './SaveConfirmation';
import { ResumePrompt } from './ResumePrompt';
import { usePartialSave } from '../../hooks/usePartialSave';
import { NON_INPUT_FIELD_TYPES } from '../../types/forms';
import { FormLogo } from './FormLogo';
import { OneFormBadge } from './OneFormBadge';
import { getAlignmentClass } from '../../lib/getAlignmentClass';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';

// Safe resolver for field.config.backgroundImage (only http(s) URLs allowed)
function resolveBgImageUrl(bg?: string): string | null {
  if (!bg) return null;
  try {
    const parsed = new URL(bg);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    return encodeURI(bg);
  } catch {
    return null;
  }
}

// ─── Redirect Screen (countdown + auto-redirect) ───
const RedirectScreen: React.FC<{
  themeConfig: import('../../types/forms').ThemeConfig;
  isPreview?: boolean;
  url: string;
  delay: number;
  openInNewTab: boolean;
  hideCountdown?: boolean;
  label: string;
  logoUrl?: string;
  logoPosition?: LogoPosition;
  logoMargin?: number;
  logoScale?: number;
  alignment?: FieldAlignment;
}> = ({ themeConfig, isPreview, url, delay, openInNewTab, hideCountdown, label, logoUrl, logoPosition, logoMargin, logoScale, alignment }) => {
  const [countdown, setCountdown] = useState(delay);
  const hasRedirected = useRef(false);

  useEffect(() => {
    setCountdown(delay);
    hasRedirected.current = false;
  }, [delay, url]);

  useEffect(() => {
    if (isPreview || !url || hasRedirected.current) return;

    if (countdown <= 0) {
      hasRedirected.current = true;
      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
      return;
    }

    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, isPreview, url, openInNewTab]);

  return (
    <div
      className={`${isPreview ? 'h-full' : 'h-dvh'} relative flex flex-col items-center justify-center p-4 overflow-hidden`}
      style={{ backgroundColor: themeConfig.backgroundColor, fontFamily: `'${themeConfig.fontFamily}', sans-serif` }}
    >
      <div className={`${isPreview ? 'absolute' : 'fixed'} inset-0 z-0 overflow-hidden pointer-events-none`}>
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px] mix-blend-screen" />
      </div>

      <FormLogo
        logoUrl={logoUrl}
        logoPosition={logoPosition}
        logoMargin={logoMargin}
        logoScale={logoScale}
        variant="absolute"
      />

      <div className={`relative z-10 liquid-glass rounded-3xl p-10 max-w-md ${getAlignmentClass(alignment ?? 'center')}`}>
        <FormLogo
          logoUrl={logoUrl}
          logoPosition={logoPosition}
          logoMargin={logoMargin}
          logoScale={logoScale}
          variant="inline"
        />
        <div className="w-20 h-20 mx-auto bg-primary-500/20 rounded-full flex items-center justify-center mb-6">
          <ExternalLink className="w-10 h-10 text-primary-400" />
        </div>
        <h1 className="text-2xl font-bold mb-2" style={{ color: themeConfig.textColor }}>
          {label}
        </h1>
        {isPreview ? (
          <p className="text-sm" style={{ color: themeConfig.textColor, opacity: 0.5 }}>
            Redirecionamento desabilitado no preview
          </p>
        ) : url ? (
          <>
            <p style={{ color: themeConfig.textColor, opacity: 0.6 }}>
              {hideCountdown || countdown <= 0 ? 'Redirecionando...' : `Redirecionando em ${countdown}s...`}
            </p>
            <p className="text-sm mt-2 truncate" style={{ color: themeConfig.textColor, opacity: 0.4 }}>
              {url}
            </p>
          </>
        ) : (
          <p style={{ color: themeConfig.textColor, opacity: 0.6 }}>
            Nenhuma URL configurada
          </p>
        )}
      </div>
    </div>
  );
};

interface FormTypeformProps {
  form: Form;
  onSubmit: (data: Record<string, any>, scores?: Record<string, any> | null) => Promise<{ error: string | null }>;
  isPreview?: boolean;
}

const NON_NAVIGABLE_TYPES: FieldType[] = ['welcome_screen', 'thank_you_screen', 'heading', 'paragraph'];

// saas-1.24-hotfix3: dica de atalho aparece apenas em campos de texto — o teclado já está em uso ali.
// Demais campos (radio/checkbox/picture/rating/etc.) usam click/touch como método primário — hint seria ruidoso.
// Enter avança; em textarea (texto longo) Enter quebra linha e o avanço é Ctrl/⌘+Enter.
const ENTER_HINT_FIELD_TYPES: FieldType[] = ['text', 'email', 'phone', 'url', 'number', 'textarea'];

// Duração da animação de entrada da pergunta. Espelha `.animate-typeform-up` e
// `.animate-typeform-down` em styles.css — se mudar lá, mude aqui.
const SLIDE_TRANSITION_MS = 200;

export const FormTypeform: React.FC<FormTypeformProps> = ({ form, onSubmit, isPreview }) => {
  const themeConfig = form.settings?.themeConfig || DEFAULT_THEME_CONFIG;
  useThemeFont(themeConfig.fontFamily);
  useLockBodyScroll(!isPreview);

  // Decisão do dono (28/08/2026): o teclado PODE cobrir o botão OK. O único
  // requisito é o campo em digitação continuar visível — quem cuida disso é o
  // scrollIntoView instantâneo do useScrollIntoViewOnFocus, nos campos.
  //
  // Removido daqui: o override de `height` a partir do visualViewport (encolhia
  // o container para manter o footer acima do teclado no iOS), o translateY que
  // compensava o pan do viewport, e a transição de altura. Os três juntos davam
  // ~850ms de reorganização visível a cada foco.

  const [formData, setFormData] = useState<Record<string, any>>({});
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isSavingExit, setIsSavingExit] = useState(false);
  // saas-1.24.A.1 hotfix: refs para scroll-reset entre perguntas (Bug 2) + detect overflow (Bug 4)
  const mainScrollRef = useRef<HTMLElement | null>(null);
  const optionsScrollerRef = useRef<HTMLDivElement | null>(null);
  const [optionsHaveOverflow, setOptionsHaveOverflow] = useState(false);
  const [showAutoSaveToast, setShowAutoSaveToast] = useState(false);
  const hasShownAutoSaveToast = useRef(false);
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Partial save integration (Story 5.2 / 5.4)
  const partialSaveEnabled = !!form.settings?.partialSubmit?.enabled && !isPreview;
  const partialSave = usePartialSave({
    formId: form.id,
    enabled: partialSaveEnabled,
    cookieExpiryDays: form.settings?.partialSubmit?.cookieExpiryDays,
  });

  // Auto-save toast — show once on first background save (AC3)
  useEffect(() => {
    if (partialSave.lastSavedAt && !hasShownAutoSaveToast.current && !showSaveConfirmation) {
      hasShownAutoSaveToast.current = true;
      setShowAutoSaveToast(true);
      const t = setTimeout(() => setShowAutoSaveToast(false), 2000);
      return () => clearTimeout(t);
    }
  }, [partialSave.lastSavedAt, showSaveConfirmation]);

  // ── Resume flow (Story 5.3) ──
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [resumeIndex, setResumeIndex] = useState<number | undefined>(undefined);

  // Show resume prompt when existing session is detected
  useEffect(() => {
    if (partialSave.hasExistingSession && partialSave.existingSessionData && !isPreview) {
      setShowResumePrompt(true);
    }
  }, [partialSave.hasExistingSession, partialSave.existingSessionData, isPreview]);

  // Resolve form pages once (used by both the conditional-logic hook and the
  // navigation hook below). Passed unfiltered to useConditionalLogic so the
  // page-level branching map and validation see the full graph.
  const allResolvedPages = useMemo(() => resolvePages(form), [form]);

  // Conditional logic: field visibility, branching, progress, rule calculations
  const {
    visibleFields,
    nextFieldMap,
    nextPageMap,
    ruleCalculations,
    calculateProgress,
    calculatePageProgress,
    flowValidation,
  } = useConditionalLogic({
    fields: form.fields,
    formData,
    pages: allResolvedPages,
  });

  // Scoring engine (includes rule-based calculations)
  const { scores, isEnabled: scoringEnabled } = useFormScoring({
    fields: form.fields,
    formData,
    scoringConfig: form.settings?.scoring,
    ruleCalculations,
  });

  // Validate a single field (uses ref to always read latest formData)
  const validateField = useCallback(
    (field: FormField): string | null => {
      return validateFieldValue(field, formDataRef.current[field.id]);
    },
    []
  );

  // Scroll + focus on the first invalid field in a multi-question page
  const handlePageValidationError = useCallback((fieldId: string, error: string) => {
    setErrors((prev) => ({ ...prev, [fieldId]: error }));

    // Scroll to the field container
    const el = document.querySelector(`[data-field-id="${fieldId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Focus the input after scroll animation finishes
      setTimeout(() => {
        const input = el.querySelector('input, textarea, select') as HTMLElement | null;
        if (input) input.focus();
      }, 300);
    }
  }, []);

  // Submit handler (uses ref to always read latest formData)
  // `silent`: grava a resposta sem trocar a tela nem disparar o redirect do
  // formulário — usado pelo slide redirect_url, que já é a tela final do branch.
  const handleSubmit = useCallback(async (options?: { silent?: boolean }) => {
    // Reentrância: Enter/tecla "Ir" do teclado virtual, o timer de auto-advance
    // e o botão "Enviar" podem disparar em paralelo enquanto o request está em
    // voo (isSubmitting via state chega tarde) — só o primeiro pode inserir.
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    // O timer de auto-advance da última pergunta chamaria goNext → onSubmit de novo
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }

    setIsSubmitting(true);
    const result = await onSubmit(formDataRef.current, scores);
    setIsSubmitting(false);

    if (result.error) {
      submitLockRef.current = false; // falhou → libera o retry manual
      setErrors({ _form: result.error });
      return;
    }
    // Sucesso: lock permanece — Enter na thank-you screen (listener global
    // continua montado) não pode reenviar a resposta.

    // Slide redirect_url já está na tela e leva o respondente embora sozinho:
    // não troca para a thank-you nem usa o redirect global do formulário.
    if (options?.silent) return;

    const redirectTarget = form.settings?.redirectUrl || form.redirect_url;
    if (redirectTarget) {
      try {
        const url = new URL(redirectTarget);
        if (url.protocol === 'https:' || url.protocol === 'http:') {
          window.location.href = redirectTarget;
        } else {
          setIsSubmitted(true);
        }
      } catch {
        setIsSubmitted(true);
      }
    } else {
      setIsSubmitted(true);
    }
  }, [scores, form.redirect_url, form.settings?.redirectUrl, onSubmit]);

  // (conditional logic hook called above with scoring)

  // Compute input-only fields for resume counting
  const inputFields = useMemo(
    () => visibleFields.filter(f => !NON_INPUT_FIELD_TYPES.includes(f.type)),
    [visibleFields]
  );

  // Resume: compute answered count + resume index
  const resumeStats = useMemo(() => {
    const sessionData = partialSave.existingSessionData;
    if (!sessionData) return { answeredCount: 0, resumeIdx: 0 };

    let answered = 0;
    for (const f of inputFields) {
      const val = sessionData[f.id];
      if (val !== undefined && val !== null && val !== '') answered++;
    }

    const navigableInputs = visibleFields.filter(f => !NON_NAVIGABLE_TYPES.includes(f.type));
    let resumeIdx = 0;
    for (let i = 0; i < navigableInputs.length; i++) {
      const val = sessionData[navigableInputs[i].id];
      if (val === undefined || val === null || val === '') {
        resumeIdx = i;
        break;
      }
      resumeIdx = i + 1;
    }
    resumeIdx = Math.min(resumeIdx, Math.max(0, navigableInputs.length - 1));

    return { answeredCount: answered, resumeIdx };
  }, [partialSave.existingSessionData, inputFields, visibleFields]);

  // Resume handlers
  const handleResume = useCallback(() => {
    const sessionData = partialSave.existingSessionData;
    if (sessionData) {
      const validData: Record<string, any> = {};
      const fieldIds = new Set(visibleFields.map(f => f.id));
      for (const [key, val] of Object.entries(sessionData)) {
        if (fieldIds.has(key)) validData[key] = val;
      }
      setFormData(validData);
      setResumeIndex(resumeStats.resumeIdx);
    }
    setShowResumePrompt(false);
  }, [partialSave.existingSessionData, visibleFields, resumeStats.resumeIdx]);

  const handleStartFresh = useCallback(() => {
    partialSave.clearSession();
    setFormData({});
    setResumeIndex(undefined);
    setShowResumePrompt(false);
  }, [partialSave]);

  // Resolve fields into pages (Story 5.5 — page-level navigation).
  // Filter by visibility for the renderer; allResolvedPages (above) keeps the
  // full graph for branching map computation.
  const resolvedPages = useMemo(() => {
    const visibleIds = new Set(visibleFields.map((f) => f.id));
    return allResolvedPages
      .map((rp) => ({ ...rp, fields: rp.fields.filter((f) => visibleIds.has(f.id)) }))
      .filter((rp) => rp.fields.length > 0);
  }, [allResolvedPages, visibleFields]);

  const nav = useTypeformNavigation({
    fields: visibleFields,
    formData,
    validateField,
    onSubmit: handleSubmit,
    nextFieldMap: flowValidation.hasLoops ? undefined : nextFieldMap,
    calculateProgress: flowValidation.hasLoops ? undefined : calculateProgress,
    initialIndex: resumeIndex,
    pages: resolvedPages,
    onPageValidationError: handlePageValidationError,
    // Story 5.7: page-level branching. Disabled when loops detected (warning state).
    nextPageMap: flowValidation.hasLoops ? undefined : nextPageMap,
    calculatePageProgress: flowValidation.hasLoops ? undefined : calculatePageProgress,
  });

  // Page-level aliases from hook
  const { currentPageIndex, currentPage: currentResolvedPage, isMultiFieldPage, currentField: navCurrentField } = nav;

  // O slide redirect_url é terminal: o navegador sai da página segundos depois.
  // A resposta precisa ser gravada AO CHEGAR nele — sem isso o branch que termina
  // em redirect perdia a resposta inteira, porque o envio só acontecia no fim do
  // fluxo sequencial, que nunca era alcançado.
  const redirectSlideId = navCurrentField?.type === 'redirect_url' ? navCurrentField.id : null;
  useEffect(() => {
    if (!redirectSlideId || isPreview) return;
    void handleSubmit({ silent: true });
  }, [redirectSlideId, isPreview, handleSubmit]);

  // saas-1.24.A.1 hotfix Bug 2: reset scroll do main + scroller interno ao mudar pergunta/página
  // (instant para evitar scroll animado que confunde o respondente entre slides)
  useEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    optionsScrollerRef.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [navCurrentField?.id, currentPageIndex]);

  // Autofoco no campo ao entrar na pergunta — o respondente digita direto, sem tocar
  // no campo (mandato user 2026-08-14). Só campos de digitação: radio/checkbox/rating
  // etc. respondem por click/tecla e receber foco ali só atrapalharia a leitura.
  // preventScroll: o reset de scroll acima é quem manda na posição.
  //
  // ADIADO até o fim da animação de entrada: no celular o focus() abre o teclado
  // virtual, e a subida dele (250–400ms no iOS) ocupa a thread principal
  // exatamente enquanto a animação da pergunta deveria rodar. Ela perdia os
  // primeiros frames e o efeito era "trava e já aparece na pergunta nova".
  // Mantém o "digita direto"; só troca a ordem.
  useEffect(() => {
    const timer = setTimeout(() => {
      const el = mainScrollRef.current?.querySelector<HTMLElement>(
        'input[type="text"], input[type="email"], input[type="tel"], input[type="url"], input[type="number"], input:not([type]), textarea'
      );
      el?.focus({ preventScroll: true });
    }, SLIDE_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [navCurrentField?.id, currentPageIndex]);

  // saas-1.24.A.1 hotfix Bug 4: detectar se scroller interno realmente tem overflow
  // (mask gradient só deve aparecer quando há rolagem real — evita crop seco em listas curtas)
  // mobile-ui.3.C AC4: recomputa em resize/orientationchange (mesmo cálculo; rAF debounce)
  useEffect(() => {
    const el = optionsScrollerRef.current;
    if (!el) {
      setOptionsHaveOverflow(false);
      return;
    }
    // requestAnimationFrame garante que layout terminou de re-renderizar (TypeformTransition + renderField)
    const check = () => {
      if (!optionsScrollerRef.current) return;
      setOptionsHaveOverflow(optionsScrollerRef.current.scrollHeight > optionsScrollerRef.current.clientHeight + 1);
    };
    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(check);
    };
    schedule();
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
    };
  }, [navCurrentField?.id, currentPageIndex]);

  // Directional wrappers for transition animation
  const goNext = useCallback(() => {
    setDirection('forward');
    nav.goNext();
  }, [nav]);

  const goPrevious = useCallback(() => {
    setDirection('backward');
    nav.goPrevious();
  }, [nav]);

  // Handle field change with auto-advance + partial save
  const handleFieldChange = useCallback(
    (fieldId: string, value: any) => {
      const newData = { ...formDataRef.current, [fieldId]: value };
      setFormData(newData);

      // Clear error
      setErrors((prev) => {
        const n = { ...prev };
        delete n[fieldId];
        return n;
      });
      nav.clearError();

      // Partial save (debounced)
      if (partialSaveEnabled) {
        partialSave.savePartial(newData, fieldId);
      }

      // Cancel pending auto-advance
      if (autoAdvanceTimer.current) {
        clearTimeout(autoAdvanceTimer.current);
        autoAdvanceTimer.current = null;
      }

      // Auto-advance for single-selection fields — disabled for multi-field pages (AC3/AC4)
      const field = nav.currentField;
      if (field && nav.shouldAutoAdvance(field.type)) {
        // Validate before auto-advancing
        const checkRequired = field.required && (!value || (Array.isArray(value) && value.length === 0));
        if (!checkRequired) {
          autoAdvanceTimer.current = setTimeout(() => {
            setDirection('forward');
            nav.goNext();
          }, 400);
        }
      }
    },
    [formData, nav, isMultiFieldPage, partialSaveEnabled, partialSave]
  );

  // Save & Exit handler
  const handleSaveExit = useCallback(async () => {
    setIsSavingExit(true);
    await partialSave.flush();
    setIsSavingExit(false);
    setShowSaveConfirmation(true);
  }, [partialSave]);

  const handleResumeSave = useCallback(() => {
    setShowSaveConfirmation(false);
  }, []);

  // Keyboard shortcuts: A-Z to select radio/checkbox options
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        (e.target instanceof HTMLElement && e.target.isContentEditable)
      ) return;

      if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return;

      const field = nav.currentField;
      if (!field || !field.options?.length) return;
      if (field.type !== 'radio' && field.type !== 'checkbox') return;

      const key = e.key.toUpperCase();
      const index = key.charCodeAt(0) - 65; // A=0, B=1, ...
      if (index < 0 || index >= field.options.length) return;

      e.preventDefault();
      const opt = normalizeFieldOption(field.options[index]);

      if (field.type === 'radio') {
        handleFieldChange(field.id, opt.value);
      } else {
        const current: string[] = formDataRef.current[field.id] || [];
        const newValue = current.includes(opt.value)
          ? current.filter((v: string) => v !== opt.value)
          : [...current, opt.value];
        handleFieldChange(field.id, newValue);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [nav.currentField, handleFieldChange]);

  // Render a single field
  const renderField = (field: FormField) => {
    const value = formData[field.id];
    const error = errors[field.id] || (field.id === nav.currentField?.id ? nav.validationError : null);
    const onChange = (newValue: any) => handleFieldChange(field.id, newValue);
    const commonProps = { field, value, onChange, error, themeConfig };

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
      case 'number':
        return <TextField {...commonProps} />;
      case 'textarea':
        return <TextAreaField {...commonProps} />;
      case 'select':
        return <SelectField {...commonProps} />;
      case 'checkbox':
        return <CheckboxField {...commonProps} value={value || []} />;
      case 'radio':
        return <RadioField {...commonProps} />;
      case 'date':
      case 'datetime':
      case 'time':
        return <DateField {...commonProps} />;
      case 'file':
        return <FileUploadField {...commonProps} />;
      case 'paragraph':
        return <p style={{ color: themeConfig.textColor, opacity: 0.6 }}>{field.placeholder}</p>;
      case 'opinion_scale':
        return <OpinionScaleField {...commonProps} />;
      case 'nps':
        return <NPSField {...commonProps} />;
      case 'rating':
        return <RatingField {...commonProps} />;
      case 'picture_choice':
        return <PictureChoiceField {...commonProps} />;
      case 'yes_no':
        return <YesNoField {...commonProps} />;
      case 'statement':
        return <StatementField {...commonProps} />;
      case 'welcome_screen':
        return <WelcomeScreenField {...commonProps} />;
      case 'thank_you_screen':
        return <ThankYouScreenField {...commonProps} />;
      case 'redirect_url':
        return <RedirectUrlField field={field} />;
      default:
        return <TextField {...commonProps} />;
    }
  };

  // ─── Save Confirmation Screen ───
  if (showSaveConfirmation) {
    return (
      <SaveConfirmation
        current={(nav.currentPageIndex !== undefined && nav.totalPages > 0 ? nav.currentPageIndex : nav.currentIndex) + 1}
        total={nav.totalQuestions}
        themeConfig={themeConfig}
        onResume={handleResumeSave}
        isPreview={isPreview}
      />
    );
  }

  // ─── Resume Prompt (Story 5.3) ───
  if (showResumePrompt && partialSave.existingSessionData) {
    return (
      <ResumePrompt
        themeConfig={themeConfig}
        answeredCount={resumeStats.answeredCount}
        totalCount={inputFields.length}
        lastSavedAt={partialSave.existingSessionUpdatedAt}
        onResume={handleResume}
        onStartFresh={handleStartFresh}
      />
    );
  }

  // ─── Welcome Screen ───
  if (nav.isWelcomeScreen) {
    const welcomeField = form.fields.find((f) => f.type === 'welcome_screen');
    const buttonText = (welcomeField?.config as WelcomeScreenConfig)?.buttonText || 'Começar';

    return (
      <div className={`${isPreview ? 'h-full' : 'h-dvh'} relative flex flex-col overflow-hidden`} style={{ backgroundColor: themeConfig.backgroundColor, fontFamily: `'${themeConfig.fontFamily}', sans-serif` }}>
        {/* Background blob */}
        <div className={`${isPreview ? 'absolute' : 'fixed'} inset-0 z-0 overflow-hidden pointer-events-none`}>
          <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[100px] mix-blend-screen" />
        </div>

        {/* Logo absolute (6 posições) — mobile-ui.6.C C2: escondido no short (sobrepõe texto/CTA
            em landscape); instância static abaixo do CTA assume */}
        <div className="short:hidden">
          <FormLogo
            logoUrl={form.settings?.logoUrl}
            logoPosition={form.settings?.logoPosition}
            logoMargin={form.settings?.logoMargin}
            logoScale={form.settings?.logoScale}
            variant="absolute"
          />
        </div>

        {/* Centered content (mobile-ui.1.C D1: scroller externo overflow-y-auto + my-auto no wrapper —
            centraliza quando cabe, rola a partir do TOPO quando não cabe; NUNCA justify-center no scroller) */}
        <main className="relative z-10 flex-1 min-h-0 flex flex-col items-center overflow-y-auto overscroll-contain px-4 md:px-8">
          <Reveal variant="zoom-in" className="my-auto w-full max-w-xl py-8 short:py-3">
            <div className={`w-full max-w-xl ${getAlignmentClass(welcomeField?.alignment ?? 'center')}`}>
              {/* Logo inline — apenas middle-center */}
              <FormLogo
                logoUrl={form.settings?.logoUrl}
                logoPosition={form.settings?.logoPosition}
                logoMargin={form.settings?.logoMargin}
                logoScale={form.settings?.logoScale}
                variant="inline"
              />
              <h1 className="text-3xl md:text-4xl short:text-xl font-bold mb-4 short:mb-2" style={{ color: themeConfig.textColor }}>
                {welcomeField?.label || form.title}
              </h1>
              {welcomeField?.helpText && (
                <p className="text-lg short:text-sm mb-8 short:mb-4" style={{ color: themeConfig.textColor, opacity: 0.6 }}>{welcomeField.helpText}</p>
              )}
              <button
                type="button"
                onClick={goNext}
                style={getButtonStyles(themeConfig, 'primary', 'lg')}
                className="font-medium transition-all duration-200"
              >
                {buttonText}
              </button>

              {/* mobile-ui.6.C C2: no short o logo sai do absoluto e entra no fluxo ABAIXO do CTA */}
              <div className="hidden short:block">
                <FormLogo
                  logoUrl={form.settings?.logoUrl}
                  logoPosition={form.settings?.logoPosition}
                  logoMargin={form.settings?.logoMargin}
                  logoScale={form.settings?.logoScale}
                  variant="static"
                />
              </div>
            </div>
          </Reveal>
        </main>
      </div>
    );
  }

  // ─── Thank You Screen ───
  if (isSubmitted) {
    const thankYouField = form.fields.find((f) => f.type === 'thank_you_screen');

    return (
      <div className={`${isPreview ? 'h-full' : 'h-dvh'} relative flex flex-col overflow-hidden`} style={{ backgroundColor: themeConfig.backgroundColor, fontFamily: `'${themeConfig.fontFamily}', sans-serif` }}>
        {/* Background blob (green tint) */}
        <div className={`${isPreview ? 'absolute' : 'fixed'} inset-0 z-0 overflow-hidden pointer-events-none`}>
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-green-600/10 rounded-full blur-[100px] mix-blend-screen" />
        </div>

        {/* Logo absolute (6 posições) */}
        <FormLogo
          logoUrl={form.settings?.logoUrl}
          logoPosition={form.settings?.logoPosition}
          logoMargin={form.settings?.logoMargin}
          logoScale={form.settings?.logoScale}
          variant="absolute"
        />

        {/* mobile-ui.1.C D1: mesmo padrão do Welcome — scroller externo + my-auto (ScoreResult com N variáveis rola) */}
        <main className="relative z-10 flex-1 min-h-0 flex flex-col items-center overflow-y-auto overscroll-contain px-4">
          <Reveal variant="zoom-in" className="my-auto py-4">
            <div className={`relative z-10 liquid-glass rounded-3xl p-10 max-w-md ${getAlignmentClass(thankYouField?.alignment ?? 'center')}`}>
              {/* Logo inline — apenas middle-center */}
              <FormLogo
                logoUrl={form.settings?.logoUrl}
                logoPosition={form.settings?.logoPosition}
                logoMargin={form.settings?.logoMargin}
                logoScale={form.settings?.logoScale}
                variant="inline"
              />
              <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-400" />
              </div>
              {/* Campo thank_you_screen personalizado tem precedência: label = título,
                  helpText = mensagem. Antes só o alignment dele era lido e o título
                  ficava fixo — a tela custom do criador nunca aparecia. */}
              <h1 className="text-2xl font-bold mb-4" style={{ color: themeConfig.textColor }}>
                {thankYouField?.label || 'Enviado com sucesso!'}
              </h1>
              <p style={{ color: themeConfig.textColor, opacity: 0.6 }}>
                {thankYouField?.helpText || form.thank_you_message || 'Obrigado por enviar o formulário!'}
              </p>
              {scoringEnabled && form.settings?.scoring?.showResultToRespondent && scores && (
                <ScoreResult
                  scores={scores}
                  variables={form.settings.scoring.variables}
                />
              )}
            </div>
          </Reveal>
        </main>
      </div>
    );
  }

  // ─── Redirect URL Screen ───
  if (nav.currentField?.type === 'redirect_url') {
    const redirectConfig = nav.currentField.config as RedirectUrlConfig | undefined;
    const redirectUrl = redirectConfig?.url || '';
    const redirectDelay = redirectConfig?.delay ?? 0;
    const openInNewTab = redirectConfig?.openInNewTab ?? false;

    return (
      <RedirectScreen
        themeConfig={themeConfig}
        isPreview={isPreview}
        url={redirectUrl}
        delay={redirectDelay}
        openInNewTab={openInNewTab}
        hideCountdown={redirectConfig?.hideCountdown ?? false}
        label={nav.currentField.label}
        logoUrl={form.settings?.logoUrl}
        logoPosition={form.settings?.logoPosition}
        logoMargin={form.settings?.logoMargin}
        logoScale={form.settings?.logoScale}
        alignment={nav.currentField?.alignment}
      />
    );
  }

  // ─── Question Screen ───
  const currentField = nav.currentField;
  const fieldError = currentField
    ? errors[currentField.id] || nav.validationError
    : null;

  // Background image da pergunta atual (Story saas-1.6)
  const currentBgUrl = resolveBgImageUrl(currentField?.config?.backgroundImage);
  const currentBgBlur = currentField?.config?.backgroundBlur ?? 0;
  const currentBgOpacity = currentField?.config?.backgroundOpacity ?? 100;

  return (
    <div
      className={`${isPreview ? 'h-full' : 'h-dvh'} relative flex flex-col overflow-hidden`}
      style={{
        backgroundColor: themeConfig.backgroundColor,
        fontFamily: `'${themeConfig.fontFamily}', sans-serif`,
      }}
    >
      {/* Camada absoluta de fundo — filtros aplicam apenas aqui (Story saas-1.6) */}
      {currentBgUrl && (
        <div
          className={`${isPreview ? 'absolute' : 'fixed'} inset-0 z-0 pointer-events-none`}
          style={{
            backgroundImage: `url("${currentBgUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: currentBgBlur > 0 ? `blur(${currentBgBlur}px)` : undefined,
            opacity: currentBgOpacity / 100,
          }}
        />
      )}

      {/* Background blob */}
      <div className={`${isPreview ? 'absolute' : 'fixed'} inset-0 z-0 overflow-hidden pointer-events-none`}>
        <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[100px] mix-blend-screen" />
      </div>

      {/* Logo absolute — apenas top-* nesta tela (bottom-* migra para variant="footer-inline" no <footer>).
          saas-1.24.A.1 hotfix: excludeAbsoluteBottom previne duplicação quando logoPosition='bottom-*'. */}
      <FormLogo
        logoUrl={form.settings?.logoUrl}
        logoPosition={form.settings?.logoPosition}
        logoMargin={form.settings?.logoMargin}
        logoScale={form.settings?.logoScale}
        variant="absolute"
        excludeAbsoluteBottom
      />

      {/* Header with progress (saas-1.23.B: flex-shrink-0 in app-shell).
          mobile-ui.6.D D3: short:pt explícito no grupo pt — mantém o floor 0.5rem do short:py-2
          da 6.C mas preserva a semântica safe-area-inset-top (que o py-2 descartava). */}
      <header className="flex-shrink-0 relative z-20 p-4 md:p-6 short:py-2 pt-[max(1rem,env(safe-area-inset-top))] short:pt-[max(0.5rem,env(safe-area-inset-top))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
        <TypeformProgress
          current={nav.currentPageIndex !== undefined && nav.totalPages > 0 ? nav.currentPageIndex : nav.currentIndex}
          total={nav.totalQuestions}
          progress={nav.progress}
          accentColor={themeConfig.buttonBgColor}
        />
      </header>

      {/* Centered content (saas-1.23.C: flex-col p/ acomodar toast sticky AC8; multi-field via pt-8).
          saas-1.24.A.1 hotfix Bug 2: ref para scroll-reset entre perguntas.
          mobile-ui.3.C AC5 (padrão 1.C D1): scroller SEM justify-center + min-h-0; centralização
          single-field via my-auto no wrapper — centraliza quando cabe, rola a partir do TOPO quando não cabe.
          O main é o scroller onde o campo focado rola para a vista. */}
      <main
        ref={mainScrollRef}
        className={`relative z-10 flex-1 min-h-0 flex flex-col px-4 md:px-8 overflow-y-auto ${isMultiFieldPage ? 'pt-8 short:pt-3' : ''}`}
      >
        {/* Auto-save toast (saas-1.23.C AC8) — sticky no topo do main, segue scroll do main */}
        {showAutoSaveToast && (
          <div
            className="sticky top-2 z-[25] mx-auto w-fit px-4 py-2 rounded-full text-xs font-medium animate-fade-in-out pointer-events-none"
            style={{ backgroundColor: `${themeConfig.buttonBgColor}20`, color: themeConfig.textColor, opacity: 0.7 }}
          >
            Progresso salvo automaticamente
          </div>
        )}
        <div className={`w-full min-w-0 mx-auto ${isMultiFieldPage ? 'max-w-3xl' : 'max-w-xl my-auto py-8 short:py-3'}`}>
          {/* Logo inline — apenas middle-center, sempre acima da pergunta */}
          <FormLogo
            logoUrl={form.settings?.logoUrl}
            logoPosition={form.settings?.logoPosition}
            logoMargin={form.settings?.logoMargin}
            logoScale={form.settings?.logoScale}
            variant="inline"
          />

          {/* Global error */}
          {errors._form && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {errors._form}
            </div>
          )}

          <TypeformTransition
            direction={direction}
            currentKey={currentResolvedPage?.page.id || currentField?.id || 'empty'}
          >
            {isMultiFieldPage && currentResolvedPage ? (
              /* ── Multi-field page: render all fields in grid ── */
              <MultiQuestionSlide
                fields={currentResolvedPage.fields}
                page={currentResolvedPage.page}
                themeConfig={themeConfig}
                errors={errors}
                renderField={renderField}
              />
            ) : (
              /* ── Single-field page: original typeform rendering ── */
              <>
                {/* Context fields (heading/paragraph that precede the input) */}
                {nav.currentContextFields.map((ctxField) => (
                  <div key={ctxField.id} className="mb-4">
                    {ctxField.type === 'heading' ? (
                      <h2 className="text-xl md:text-2xl short:text-base font-semibold" style={{ color: themeConfig.textColor, opacity: 0.8 }}>
                        {ctxField.label}
                      </h2>
                    ) : (
                      <p className="text-base" style={{ color: themeConfig.textColor, opacity: 0.6 }}>{ctxField.placeholder || ctxField.label}</p>
                    )}
                  </div>
                ))}

                {currentField && (
                  <div className={getAlignmentClass(currentField.alignment)}>
                    {/* Field label (larger typography for typeform) */}
                    {currentField.type !== 'statement' && (
                      <label className="block text-2xl md:text-3xl short:text-lg font-bold mb-2" style={{ color: themeConfig.textColor }}>
                        {currentField.label}
                        {currentField.required && (
                          <span className="text-red-400 ml-1">*</span>
                        )}
                      </label>
                    )}

                    {/* Help text */}
                    {currentField.helpText && (
                      <p className="text-base short:text-sm mb-6 short:mb-3" style={{ color: themeConfig.textColor, opacity: 0.6 }}>
                        {currentField.helpText}
                      </p>
                    )}

                    {/* Field input — saas-1.24.A.1 hotfix Bug 3+4:
                        - max-h reduzida (mín 3 / máx ~5 opções + 1 parcial); user mandato 2026-05-04
                        - fade gradativo SUAVE 80px aplicado APENAS quando há overflow real (optionsHaveOverflow)
                        - md:mask-none + md:max-h-none preservam comportamento desktop (main absorve scroll) */}
                    <div
                      ref={optionsScrollerRef}
                      className="mt-4 short:mt-2 max-h-[min(380px,50vh)] overflow-y-auto md:max-h-none md:overflow-y-visible md:mask-none"
                      style={optionsHaveOverflow ? {
                        maskImage: 'linear-gradient(to bottom, black calc(100% - 80px), transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 80px), transparent 100%)',
                      } : undefined}
                    >
                      {renderField(currentField)}
                    </div>

                    {/* Error message */}
                    {fieldError && (
                      <p className="mt-3 text-sm text-red-400">{fieldError}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </TypeformTransition>
        </div>
      </main>

      {/* Footer with navigation (saas-1.23.B: flex-shrink-0; preserves saas-1.23.A safe-area pb).
          saas-1.24-hotfix3 ordem: TypeformNavigation (botões + dica de atalho) → Logo footer-inline → OneFormBadge.
          A dica aparece SÓ em campos de texto (showEnterHint) — TypeformNavigation controla. */}
      {/* mobile-ui.6.D D3: a 6.C só apertou o topo (short:pt-2) — o pb-[max(1rem,safe-area)] base
          seguia sozinho no padding-bottom (1rem no short); short:pb aperta pra max(0.5rem,safe-area) */}
      <footer className="flex-shrink-0 relative z-20 p-4 md:p-6 short:pt-2 pb-[max(1rem,env(safe-area-inset-bottom))] short:pb-[max(0.5rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
        <TypeformNavigation
          onNext={goNext}
          onPrevious={goPrevious}
          isFirst={nav.isFirstQuestion && !form.fields.some((f) => f.type === 'welcome_screen')}
          isLast={nav.isLastQuestion}
          isSubmitting={isSubmitting}
          submitText={form.settings?.submitButtonText}
          nextText={form.settings?.nextButtonText}
          themeConfig={themeConfig}
          onSaveExit={partialSaveEnabled ? handleSaveExit : undefined}
          isSavingExit={isSavingExit}
          canSaveExit={Object.keys(formData).length > 0}
          showEnterHint={ENTER_HINT_FIELD_TYPES.includes(currentField?.type ?? 'text')}
          isLongText={currentField?.type === 'textarea'}
        />

        {/* Logo footer-inline — bottom-* renderiza ANTES do OneFormBadge (mandato user 2026-05-04 atualizado) */}
        <FormLogo
          logoUrl={form.settings?.logoUrl}
          logoPosition={form.settings?.logoPosition}
          logoMargin={form.settings?.logoMargin}
          logoScale={form.settings?.logoScale}
          variant="footer-inline"
        />

        {/* Branding — BP-6 OneFormBadge com gate por plano (último elemento do footer) */}
        <OneFormBadge
          ownerPlanTier={form.owner_plan_tier}
          formSettings={form.settings}
        />
      </footer>
    </div>
  );
};
